import { useEffect, useState } from "react";
import { api } from "../services/api/api";

/**
 * Feature flags read by the web app. Codes mirror the backend FeatureFlagKey enum (core).
 */
export enum FeatureFlagKey {
  TECHNICIAN_PAUSE = "technician_pause",
}

/**
 * One request per page load, shared by every caller: the interventions board reads a flag once
 * per row. Dropped on failure so the next component to mount asks again.
 */
let enabledKeys: Promise<string[]> | null = null;

function loadEnabledKeys(): Promise<string[]> {
  if (!enabledKeys) {
    // Started inside the promise so an error thrown before the request is even sent (no token
    // yet) is caught below like any other failure, instead of breaking the render.
    enabledKeys = new Promise<unknown>((resolve) => resolve(api.get("users/feature-flags")))
      .then((keys) => (Array.isArray(keys) ? (keys as string[]) : []))
      .catch(() => {
        enabledKeys = null;
        return [];
      });
  }
  return enabledKeys;
}

/** Forgets the shared answer between tests. */
export function resetFeatureFlagsForTests() {
  enabledKeys = null;
}

/**
 * Whether a feature is turned on for this backend.
 *
 * Fail-closed, like the mobile app: false while the answer is pending, when the request fails,
 * and when the key is absent (GET users/feature-flags lists only the keys that are on).
 */
export function useFeatureFlag(key: FeatureFlagKey): boolean {
  const [isEnabled, setIsEnabled] = useState(false);

  useEffect(() => {
    let isCurrent = true;
    loadEnabledKeys().then((keys) => {
      if (isCurrent) setIsEnabled(keys.includes(key));
    });
    return () => {
      isCurrent = false;
    };
  }, [key]);

  return isEnabled;
}
